import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowLeft, Mail, MessageSquare, X, ExternalLink, Send } from 'lucide-react';
import { EmptyState } from './EmptyState';
interface FeedItem {
  id: string;
  source: 'Gmail' | 'Slack';
  person: string;
  subject: string;
  preview: string;
  time: string;
  channel?: string;
  unread?: boolean;
  hasAttachment?: boolean;
  labels?: string[];
}
const FEED: FeedItem[] = [{
  id: '1',
  source: 'Gmail',
  person: 'Sarah',
  subject: 'Q2 budget approval needed',
  preview: 'Hi Matt, please review the revised budget numbers before the board meeting on Friday. I\'ve attached the updated spreadsheet with...',
  time: '2h ago',
  unread: true,
  hasAttachment: true,
  labels: ['Work', 'Finance']
}, {
  id: '2',
  source: 'Gmail',
  person: 'James',
  subject: 'Contract review for Acme Corp',
  preview: 'Matt, the Acme contract is ready for your review. Key change is the liability clause in section 4.2. Legal has already signed off on...',
  time: '4h ago',
  unread: true,
  labels: ['Work', 'Legal']
}, {
  id: '3',
  source: 'Slack',
  person: 'Tom',
  subject: 'Rate limiting issue in production',
  preview: 'Hey, we\'re seeing 429s from the /sync endpoint again. Third-party consumers are hitting it hard during peak hours. I think we need...',
  time: '2h ago',
  channel: '#engineering',
  unread: true
}, {
  id: '4',
  source: 'Slack',
  person: 'Lisa',
  subject: 'SSL cert expires Friday',
  preview: 'Heads up \u2014 the server cert expires this Friday. I have a PR ready for the auto-renewal. Can you approve it today so we can deploy...',
  time: '3h ago',
  channel: '#ops'
}, {
  id: '5',
  source: 'Gmail',
  person: 'Paul',
  subject: 'Can I have a link to the Privacy Policy?',
  preview: 'Hi Matt, I need the privacy policy link for a client proposal going out tomorrow. Quick question \u2014 is it the contextloops.com one...',
  time: 'this morning',
  labels: ['Work']
}, {
  id: '6',
  source: 'Slack',
  person: 'Sarah',
  subject: 'Budget meeting moved to 11am',
  preview: 'FYI, moved the budget review to 11am so James can join. Same room. Updated the calendar invite.',
  time: '5h ago',
  channel: '#general'
}, {
  id: '7',
  source: 'Gmail',
  person: 'Acme Legal',
  subject: 'DPA review comments',
  preview: 'Please find attached our comments on the Data Processing Agreement. We have a few concerns around data retention periods and...',
  time: 'yesterday',
  hasAttachment: true,
  labels: ['Work', 'Legal']
}, {
  id: '8',
  source: 'Slack',
  person: 'Bot',
  subject: 'Deploy completed: zenbot-web v2.4.1',
  preview: 'Successfully deployed zenbot-web v2.4.1 to production. All health checks passing. Deployment took 2m 34s.',
  time: 'yesterday',
  channel: '#deploys'
}];
const SOURCE_ICONS = {
  Gmail: Mail,
  Slack: MessageSquare
};
const TABS = ['All', 'Gmail', 'Slack'] as const;
const GMAIL_FILTERS: Record<string, string[]> = {
  Status: ['Unread', 'Starred', 'Has attachment'],
  Labels: ['Work', 'Finance', 'Legal', 'Newsletter', 'Personal'],
  When: ['Today', 'This week', 'Last 7 days']
};
const SLACK_FILTERS: Record<string, string[]> = {
  Channel: ['#engineering', '#ops', '#general', '#sales', '#deploys'],
  Type: ['Message', 'Thread', 'Bot'],
  When: ['Today', 'This week', 'Last 7 days']
};
type FilterState = Record<string, string[]>;
function FilterPills({
  filters,
  activeFilters,
  onToggle
}: {
  filters: Record<string, string[]>;
  activeFilters: FilterState;
  onToggle: (c: string, v: string) => void;
}) {
  return <div className="flex flex-wrap gap-x-6 gap-y-3 mb-6" data-id="element-1000">
      {Object.entries(filters).map(([cat, vals]) => <div key={cat} className="flex items-center gap-2 flex-wrap" data-id="element-1001">
          <span className="text-[12px] text-[#8a8680]/50 uppercase tracking-wider" data-id="element-1002">{cat}</span>
          {vals.map(v => {
        const active = (activeFilters[cat] || []).includes(v);
        return <button key={v} onClick={() => onToggle(cat, v)} className={`text-[13px] px-2.5 py-1 rounded-full transition-colors ${active ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-[#252530] text-[#8a8680] hover:text-[#e8e4df]'}`} data-id="element-1003">{v}{active && <X size={10} className="inline ml-1.5 -mt-0.5" data-id="element-1004" />}</button>;
      })}
        </div>)}
    </div>;
}
function QuickReply({
  placeholder,
  buttonLabel
}: {
  placeholder: string;
  buttonLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  if (!open) return <button onClick={() => setOpen(true)} className="flex items-center gap-2 text-[13px] text-[#8a8680] hover:text-[#e8e4df] transition-colors mt-4 focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40 rounded" data-id="element-1005"><Send size={14} data-id="element-1006" />{buttonLabel}</button>;
  return <div className="mt-4 flex items-end gap-3" data-id="element-1007">
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder={placeholder} className="flex-1 bg-[#1c1c22] text-[#e8e4df] rounded-[12px] px-4 py-3 text-[14px] outline-none placeholder:text-[#8a8680]/40 focus:ring-1 focus:ring-[#4a9a9a]/30 resize-none min-h-[80px]" autoFocus data-id="element-1008" />
      <button onClick={() => {
      setText('');
      setOpen(false);
    }} className="px-4 py-2.5 rounded-[10px] bg-[#4a9a9a] text-[#141418] text-[13px] font-medium hover:bg-[#5bb5b5] transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40" data-id="element-1009"><Send size={14} data-id="element-1010" /></button>
    </div>;
}
export function FeedView() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string>('All');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<FilterState>({});
  const [empty, setEmpty] = useState(false);
  const toggleFilter = (c: string, v: string) => {
    setActiveFilters(prev => {
      const cur = prev[c] || [];
      return {
        ...prev,
        [c]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]
      };
    });
  };
  if (viewingId) {
    const item = FEED.find(f => f.id === viewingId);
    if (item) return <FeedDetail item={item} onBack={() => setViewingId(null)} data-id="element-1011" />;
  }
  const currentFilters = activeTab === 'Gmail' ? GMAIL_FILTERS : activeTab === 'Slack' ? SLACK_FILTERS : null;
  const hasActive = Object.values(activeFilters).some(v => v.length > 0);
  const filtered = empty ? [] : FEED.filter(f => {
    if (activeTab !== 'All' && f.source !== activeTab) return false;
    if (search && !f.person.toLowerCase().includes(search.toLowerCase()) && !f.subject.toLowerCase().includes(search.toLowerCase())) return false;
    if (!hasActive) return true;
    for (const [cat, vals] of Object.entries(activeFilters)) {
      if (vals.length === 0) continue;
      if (cat === 'Channel' && f.channel && !vals.includes(f.channel)) return false;
      if (cat === 'Labels' && f.labels && !f.labels.some(l => vals.includes(l))) return false;
      if (cat === 'Status') {
        if (vals.includes('Unread') && !f.unread) return false;
        if (vals.includes('Has attachment') && !f.hasAttachment) return false;
      }
    }
    return true;
  });
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-1012">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-1013">
        <header className="mb-6" data-id="element-1014"><h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight mb-1" data-id="element-1015">Messages</h1><p className="text-[#8a8680] text-[14px]" data-id="element-1016">Conversations from Gmail and Slack.</p></header>
        <div className="relative mb-6" data-id="element-1017"><Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8680]/50" data-id="element-1018" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search messages..." className="w-full bg-[#1c1c22] text-[#e8e4df] rounded-[12px] pl-11 pr-4 py-3 text-[15px] outline-none placeholder:text-[#8a8680]/40 focus:ring-1 focus:ring-[#4a9a9a]/30" data-id="element-1019" /></div>
        <nav className="flex items-center gap-6 mb-5 border-b border-[#8a8680]/10" data-id="element-1020">
          {TABS.map(tab => {
          const isActive = activeTab === tab;
          return <button key={tab} onClick={() => {
            setActiveTab(tab);
            setActiveFilters({});
          }} className={`pb-3 text-[14px] font-medium transition-colors relative ${isActive ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-1021">{tab}{isActive && <motion.div layoutId="feedTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1022" />}</button>;
        })}
        </nav>
        {currentFilters && <FilterPills filters={currentFilters} activeFilters={activeFilters} onToggle={toggleFilter} data-id="element-1023" />}

        {filtered.length === 0 ? <EmptyState message={empty ? 'Inbox zero.' : 'No messages match.'} sub={empty ? 'Nothing to read right now.' : undefined} data-id="element-1024" /> : <div className="flex flex-col gap-2" data-id="element-1025">
            {filtered.map((item, i) => {
          const Icon = SOURCE_ICONS[item.source];
          return <motion.button key={item.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: i * 0.03
          }} onClick={() => setViewingId(item.id)} className="bg-[#1c1c22] rounded-[16px] p-5 shadow-xl shadow-black/20 text-left group hover:bg-[#1e1e25] transition-colors w-full focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40" data-id="element-1026">
                  <div className="flex items-start gap-4" data-id="element-1027">
                    <div className="w-10 h-10 rounded-full bg-[#252530] flex items-center justify-center shrink-0 mt-0.5" data-id="element-1028">{item.source === 'Slack' ? <Icon size={18} className="text-[#8a8680]" data-id="element-1029" /> : <span className="text-[#8a8680] text-sm font-medium" data-id="element-1030">{item.person.charAt(0)}</span>}</div>
                    <div className="flex-1 min-w-0" data-id="element-1031">
                      <div className="flex items-center gap-2" data-id="element-1032"><span className={`font-semibold text-[15px] ${item.unread ? 'text-[#e8e4df]' : 'text-[#e8e4df]/70'}`} data-id="element-1033">{item.person}</span>{item.channel && <span className="text-[12px] text-[#8a8680]/40" data-id="element-1034">{item.channel}</span>}{item.unread && <div className="w-1.5 h-1.5 rounded-full bg-[#4a9a9a] shrink-0" data-id="element-1035" />}</div>
                      <p className={`text-[14px] mt-0.5 ${item.unread ? 'text-[#e8e4df]/90' : 'text-[#e8e4df]/60'}`} data-id="element-1036">{item.subject}</p>
                      <p className="text-[13px] text-[#8a8680]/50 mt-1 line-clamp-1" data-id="element-1037">{item.preview}</p>
                    </div>
                    <span className="text-[12px] text-[#8a8680]/40 whitespace-nowrap shrink-0 mt-1" data-id="element-1038">{item.time}</span>
                  </div>
                </motion.button>;
        })}
          </div>}

        <button onClick={() => setEmpty(!empty)} className="fixed bottom-4 right-20 text-[10px] text-[#8a8680]/30 hover:text-[#8a8680] bg-[#1c1c22]/60 backdrop-blur-sm px-2 py-1 rounded-full" data-id="element-1039">{empty ? 'Restore demo' : 'Empty demo'}</button>
      </div>
    </div>;
}
function FeedDetail({
  item,
  onBack
}: {
  item: FeedItem;
  onBack: () => void;
}) {
  const Icon = SOURCE_ICONS[item.source];
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-1040">
      <div className="max-w-[680px] mx-auto w-full" data-id="element-1041">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680] hover:text-[#e8e4df] transition-colors mb-8 focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40 rounded" data-id="element-1042"><ArrowLeft size={16} data-id="element-1043" /><span className="text-sm" data-id="element-1044">Messages</span></button>
        <header className="mb-6" data-id="element-1045">
          <div className="flex items-center gap-3 mb-3" data-id="element-1046">
            <div className="w-10 h-10 rounded-full bg-[#252530] flex items-center justify-center" data-id="element-1047">{item.source === 'Slack' ? <Icon size={18} className="text-[#8a8680]" data-id="element-1048" /> : <span className="text-[#8a8680] text-sm font-medium" data-id="element-1049">{item.person.charAt(0)}</span>}</div>
            <div data-id="element-1050"><span className="font-semibold text-[#e8e4df]" data-id="element-1051">{item.person}</span><div className="flex items-center gap-2 mt-0.5" data-id="element-1052"><span className="text-[13px] text-[#8a8680]/50" data-id="element-1053">{item.source}</span>{item.channel && <><span className="text-[#8a8680]/20" data-id="element-1054">&middot;</span><span className="text-[13px] text-[#8a8680]/50" data-id="element-1055">{item.channel}</span></>}<span className="text-[#8a8680]/20" data-id="element-1056">&middot;</span><span className="text-[13px] text-[#8a8680]/50" data-id="element-1057">{item.time}</span></div></div>
          </div>
          <h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight" data-id="element-1058">{item.subject}</h1>
          {item.labels && <div className="flex items-center gap-2 mt-2" data-id="element-1059">{item.labels.map(l => <span key={l} className="text-[11px] text-[#8a8680]/50 bg-[#252530] px-2 py-0.5 rounded-full" data-id="element-1060">{l}</span>)}</div>}
        </header>
        <div className="bg-[#1c1c22] rounded-[16px] p-8 shadow-xl shadow-black/20" data-id="element-1061">
          <p className="text-[#e8e4df]/80 text-[15px] leading-[1.8]" data-id="element-1062">{item.preview}</p>
          <p className="text-[#e8e4df]/80 text-[15px] leading-[1.8] mt-4" data-id="element-1063">Looking forward to your thoughts on this. Let me know if you need any additional context or if we should schedule a quick call to discuss.</p>
        </div>
        <div className="flex items-center justify-between mt-4" data-id="element-1064">
          <QuickReply placeholder={item.source === 'Slack' ? 'Reply in thread...' : 'Reply to email...'} buttonLabel="Reply" data-id="element-1065" />
          <button className="flex items-center gap-1.5 text-[#8a8680]/40 hover:text-[#4a9a9a] transition-colors text-[13px]" data-id="element-1066"><ExternalLink size={14} data-id="element-1067" />View in {item.source}</button>
        </div>
      </div>
    </motion.div>;
}
export { QuickReply };